/* Espelho de `App\Domain\PixKeyType` na API. Os dois lados precisam andar
   juntos: a API normaliza a chave antes de validar, então mandar daqui o valor
   mascarado ("123.456.789-01") funciona, mas mandar um telefone sem o +55 não —
   é o `normalize` daqui que evita o 422. */

const pixKeyTypes = {
    CPF: 'cpf',
    CNPJ: 'cnpj',
    EMAIL: 'email',
    PHONE: 'phone',
    RANDOM: 'random',
};

/* A ordem é a que o produtor espera ver: CPF primeiro porque é a chave mais
   comum, chave aleatória por último porque é a menos usada. */
const pixKeyOptions = [
    { value: pixKeyTypes.CPF, label: 'CPF' },
    { value: pixKeyTypes.CNPJ, label: 'CNPJ' },
    { value: pixKeyTypes.EMAIL, label: 'E-mail' },
    { value: pixKeyTypes.PHONE, label: 'Telefone' },
    { value: pixKeyTypes.RANDOM, label: 'Chave aleatória' },
];

const masksByType = {
    [pixKeyTypes.CPF]: '999.999.999-99',
    [pixKeyTypes.CNPJ]: '99.999.999/9999-99',
    [pixKeyTypes.PHONE]: '(99) 99999-9999',
    [pixKeyTypes.EMAIL]: '',
    [pixKeyTypes.RANDOM]: '',
};

const placeholdersByType = {
    [pixKeyTypes.CPF]: '000.000.000-00',
    [pixKeyTypes.CNPJ]: '00.000.000/0000-00',
    [pixKeyTypes.PHONE]: '(00) 00000-0000',
    [pixKeyTypes.EMAIL]: 'seu@email.com',
    [pixKeyTypes.RANDOM]: '00000000-0000-0000-0000-000000000000',
};

const onlyDigits = (value) => String(value).replace(/\D/g, '');

/* O Pix guarda o telefone em E.164 e a API só aceita DDI 55, porque a conta de
   destino é sempre brasileira.

   O DDI só sai quando o tamanho prova que ele está lá: sem DDI o número tem 10
   ou 11 dígitos (DDD + 8 ou 9), com DDI tem 12 ou 13. Sem essa checagem o DDD
   55 — Santa Maria/RS — seria confundido com o DDI e comido. Mesma regra do
   `PixKeyType::normalizePhone` na API. */
function stripCountryCode(digits) {
    const hasCountryCode = digits.startsWith('55') && [12, 13].includes(digits.length);

    return hasCountryCode ? digits.slice(2) : digits;
}

function normalizePhone(value) {
    return `+55${stripCountryCode(onlyDigits(value))}`;
}

function normalize(type, value) {
    if (value === null || value === undefined) {
        return '';
    }

    const trimmed = String(value).trim();

    switch (type) {
        case pixKeyTypes.CPF:
        case pixKeyTypes.CNPJ:
            return onlyDigits(trimmed);
        case pixKeyTypes.PHONE:
            return normalizePhone(trimmed);
        case pixKeyTypes.EMAIL:
        case pixKeyTypes.RANDOM:
            return trimmed.toLowerCase();
        default:
            return trimmed;
    }
}

/* Mesmas regras do `PixKeyType::isValidKey` da API, aplicadas sobre o valor já
   normalizado — validar o mascarado daria falso negativo no CPF. */
function isValid(type, value) {
    const key = normalize(type, value);

    if (!key) {
        return false;
    }

    switch (type) {
        case pixKeyTypes.CPF:
            return /^\d{11}$/.test(key);
        case pixKeyTypes.CNPJ:
            return /^\d{14}$/.test(key);
        case pixKeyTypes.EMAIL:
            return key.length <= 77 && /^\S+@\S+\.\S+$/.test(key);
        case pixKeyTypes.PHONE:
            return /^\+55\d{10,11}$/.test(key);
        case pixKeyTypes.RANDOM:
            return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(key);
        default:
            return false;
    }
}

function invalidMessage(type) {
    switch (type) {
        case pixKeyTypes.CPF:
            return 'Informe um CPF válido';
        case pixKeyTypes.CNPJ:
            return 'Informe um CNPJ válido';
        case pixKeyTypes.EMAIL:
            return 'Informe um e-mail válido com até 77 caracteres';
        case pixKeyTypes.PHONE:
            return 'Informe um telefone com DDD';
        case pixKeyTypes.RANDOM:
            return 'A chave aleatória deve estar no formato UUID';
        default:
            return 'Selecione o tipo da chave Pix';
    }
}

/* Caminho inverso do `normalize`: a API devolve a chave crua e o input precisa
   dela no formato da máscara, senão o produtor abre a edição e vê o próprio CPF
   como um monte de dígitos. */
function toInput(type, value) {
    if (!value) {
        return '';
    }

    if (type === pixKeyTypes.PHONE) {
        return stripCountryCode(onlyDigits(value));
    }

    return String(value);
}

const maskFor = (type) => masksByType[type] || '';
const placeholderFor = (type) => placeholdersByType[type] || 'Informe a chave';
const optionFor = (type) => pixKeyOptions.find((option) => option.value === type) || null;

export default {
    types: pixKeyTypes,
    options: pixKeyOptions,
    maskFor,
    placeholderFor,
    optionFor,
    normalize,
    isValid,
    invalidMessage,
    toInput,
};
